import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import FBRLogo from "../../Images/fbrDigitalInvoicingLogo.jpg";
import {
  generateLogoInvoice,
  generateLogoOnlyInvoice,
} from "./generateLogoInvoice";
import { generateThermalInvoice } from "./generateThermalInvoice";
import { generateHalfPageInvoice } from "./generateHalfPageInvoice";
import {
  displayTwoDecimalDigits,
  section236HAmountFor,
  section236HFinalAmountFor,
  section236HLabel,
  SECTION_236H_FINAL_LABEL,
  hasSection236H,
} from "../../global/Utils";
import { formatTwoDecimals } from "../../global/money";
import { sellerPhoneRows } from "../../global/sellerPhones";
import { toWords } from "number-to-words";
import moment from "moment";

// ─── Brand palette (matches app theme) ───────────────────────────────────────
const P = {
  navy: [26, 40, 79], // #1a284f
  blue: [58, 128, 251], // #3A80FB
  lightBlue: [235, 242, 250], // soft blue tint
  white: [255, 255, 255],
  offWhite: [247, 249, 252],
  border: [220, 228, 240],
  textDark: [30, 41, 59],
  textMuted: [100, 116, 139],
  blueFade: [170, 198, 240], // muted text on dark bg
  grandTotalBg: [42, 105, 210], // slightly deeper blue for grand total row
};

export const generateInvoice = async (data) => {
  // Companies set their preferred layout on the company record; anything that
  // is not one of the named patterns falls through to the default template
  // below. "Logo Only" is the same layout as "With Company Logo" minus the
  // typed letterhead, for companies whose logo file already carries it, and
  // "Thermal Invoice" is the 80 mm roll receipt for counter printers.
  // "Half Page Invoice" is this default layout shrunk onto the top half of an
  // A4 portrait sheet.
  const pattern = data?.companyId?.invoicePattern;
  if (pattern === "With Company Logo") {
    return generateLogoInvoice(data);
  }
  if (pattern === "Logo Only") {
    return generateLogoOnlyInvoice(data);
  }
  if (pattern === "Thermal Invoice") {
    return generateThermalInvoice(data);
  }
  if (pattern === "Half Page Invoice") {
    return generateHalfPageInvoice(data);
  }

  const doc = new jsPDF({ orientation: "l", format: "a4" });

  const PW = doc.internal.pageSize.getWidth(); // 297 mm
  const PH = doc.internal.pageSize.getHeight(); // 210 mm
  const M = 14; // left / right margin
  const FOOTER_BAR_H = 9;
  const CONTENT_BOTTOM = PH - FOOTER_BAR_H - 4; // max y before footer

  let y = 0;

  const companyName = data?.companyId?.companyName || "";
  const invoiceRef = data?.FBRInvoiceRefNo || "";
  const invoiceNo = data?.invoiceNumber || "";

  // ─── Drawing helpers ─────────────────────────────────────────────────────

  /** Filled rectangle (no border) */
  const fillRect = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, "F");
  };

  /** Stroked rectangle, optional fill */
  const strokeRect = (x, ry, w, h, borderColor, bgColor = null) => {
    doc.setDrawColor(...borderColor);
    if (bgColor) {
      doc.setFillColor(...bgColor);
      doc.rect(x, ry, w, h, "FD");
    } else {
      doc.rect(x, ry, w, h, "S");
    }
  };

  /** Horizontal rule */
  const hLine = (lx, rx, ry, color = P.border) => {
    doc.setDrawColor(...color);
    doc.line(lx, ry, rx, ry);
  };

  /** Add a new page and reset y */
  const newPage = () => {
    doc.addPage();
    y = 20;
  };

  const needsNewPage = (h) => {
    if (y + h > CONTENT_BOTTOM) {
      newPage();
      return true;
    }
    return false;
  };

  // ─── HEADER BAND ─────────────────────────────────────────────────────────
  const HDR_H = 37;
  const PILL_W = 44;
  const PILL_X = PW - M - PILL_W - 32; // leave room for QR

  // Navy background
  fillRect(0, 0, PW, HDR_H, P.navy);

  // Bottom accent stripe
  fillRect(0, HDR_H - 2.5, PW, 2.5, P.blue);

  // FBR Logo (left)
  doc.addImage(FBRLogo, "JPG", M, 2.5, 30, 30);

  // Company name – centered
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...P.white);
  doc.text('Sale Tax Invoice', PW / 2, 15, { align: "center" });

  // Sub-label
  doc.setFont("Helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...P.blueFade);
  doc.text("FBR Digital Sales Tax Invoice", PW / 2, 22, { align: "center" });

  if (invoiceRef) {
    // "TAX INVOICE" pill
    fillRect(PILL_X, 7, PILL_W, 11, P.blue);
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...P.white);
    doc.text("FBR INVOICE", PILL_X + PILL_W / 2, 14.5, { align: "center" });

    // Invoice ref below pill
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...P.blueFade);
    doc.text(`Ref # ${invoiceRef}`, PILL_X + PILL_W / 2, 23, {
      align: "center",
    });

    // QR Code (far right, centred vertically in header)
    const qrDataUrl = await QRCode.toDataURL(invoiceRef, {
      width: 140,
      margin: 1,
    });
    doc.addImage(qrDataUrl, "PNG", PW - M - 27, 2, 25, 25);
    // Small label under QR
    doc.setFontSize(6);
    doc.setTextColor(...P.blueFade);
    doc.text("Scan to verify", PW - M - 27 + 12.5, 29.5, { align: "center" });
  }

  y = HDR_H + 5;

  // ─── INFO CARDS ──────────────────────────────────────────────────────────
  const CARD_HH = 8; // header strip height
  const COL_W = (PW - M * 2 - 8) / 3;
  const VAL_W = COL_W - 29;
  const ROW_GAP = 7;
  const FIRST_ROW = CARD_HH + 6; // first row's baseline, below the card top
  const cardTop = y;

  const cards = [
    {
      x: M,
      title: "SELLER INFORMATION",
      rows: [
        ["Name:", companyName],
        ["NTN/CNIC:", data?.companyId?.ntn],
        ["Province:", data?.companyId?.province],
        ["Address:", data?.companyId?.businessAddress],
        // Contact / Landline only when the company has them - never a label
        // with nothing after it.
        ...sellerPhoneRows(data?.companyId),
      ],
    },
    {
      x: M + COL_W + 4,
      title: "PURCHASER INFORMATION",
      rows: [
        ["Name:", data?.buyerId?.businessName],
        ["NTN/CNIC:", data?.buyerId?.registrationNo],
        ["Province:", data?.buyerId?.province],
        ["Address:", data?.buyerId?.address],
      ],
    },
    {
      x: M + COL_W * 2 + 8,
      title: "INVOICE DETAILS",
      rows: [
        ["Invoice #:", invoiceNo],
        ["Date:", moment(data?.date).format("DD-MMM-YYYY")],
        ["Tax Period:", moment(data?.date).format("YYYY-MM")],
        ["Type:", data?.invoiceType],
        ...(data?.purchaseOrderNo ? [["PO #:", data?.purchaseOrderNo]] : []),
      ],
    },
  ];

  // A long value wraps. The row gap already leaves room for a second line, so
  // only a third line onwards pushes the rows below it down. All three cards
  // take the height of the tallest one, and never less than the 50 mm they
  // always had - an invoice with no extra rows looks exactly as before.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  const LINE_H =
    (doc.getFontSize() * doc.getLineHeightFactor()) / doc.internal.scaleFactor;
  let contentH = 0;
  cards.forEach((card) => {
    let ry = 0;
    card.layout = card.rows.map(([label, value]) => {
      const lines = doc.splitTextToSize(String(value || "–"), VAL_W);
      const at = ry;
      ry += ROW_GAP + Math.max(0, lines.length - 2) * LINE_H;
      contentH = Math.max(contentH, at + (lines.length - 1) * LINE_H);
      return { label, lines, at };
    });
  });
  const CARD_H = Math.max(50, FIRST_ROW + contentH + 5);

  cards.forEach((card) => {
    strokeRect(card.x, cardTop, COL_W, CARD_H, P.border, P.offWhite);
    fillRect(card.x, cardTop, COL_W, CARD_HH, P.navy);

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...P.white);
    doc.text(card.title, card.x + COL_W / 2, cardTop + 5.5, {
      align: "center",
    });

    // Two-tone label : value rows
    card.layout.forEach(({ label, lines, at }) => {
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...P.textMuted);
      doc.text(label, card.x + 3, cardTop + FIRST_ROW + at);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...P.textDark);
      doc.text(lines, card.x + 27, cardTop + FIRST_ROW + at);
    });
  });

  y = cardTop + CARD_H + 5;

  // ─── SECTION LABEL before table ──────────────────────────────────────────
  fillRect(M, y, PW - M * 2, 7, P.navy);
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...P.white);
  doc.text("INVOICE ITEMS", M + 4, y + 5);

  y += 7;

  // ─── ITEMS TABLE ─────────────────────────────────────────────────────────
  const columns = [
    "#",
    "HS Code",
    "Product Description",
    "Sale Type",
    "Qty",
    "UOM",
    "Unit Rate",
    "Tax %",
    "Value Excl.",
    "Retail",
    "ST",
    "Extra",
    "Further",
    "FED",
    "WHT",
    "Disc.",
    "SRO",
    "SRO Item",
    "Amount",
  ];

  const rows = (data?.items || []).map((item, i) => [
    i + 1,
    item.hsCode || "",
    item?.itemId?.name || item?.productDescription || "",
    item?.saleType || "",
    item?.quantity || 0,
    item.uoM || "",
    formatTwoDecimals(
      (item.valueSalesExcludingST || 0) / (item?.quantity || 1),
    ),
    item.rate || 0,
    formatTwoDecimals(item.valueSalesExcludingST),
    formatTwoDecimals(item.fixedNotifiedValueOrRetailPrice),
    formatTwoDecimals(item.salesTaxApplicable),
    formatTwoDecimals(item.extraTax),
    formatTwoDecimals(item.furtherTax),
    formatTwoDecimals(item.fedPayable),
    formatTwoDecimals(item.salesTaxWithheldAtSource),
    formatTwoDecimals(item.discount),
    item.sroScheduleNo || "",
    item.sroItemSerialNo || "",
    formatTwoDecimals(item.totalValues),
  ]);

  autoTable(doc, {
    startY: y,
    head: [columns],
    body: rows,
    theme: "grid",
    styles: {
      fontSize: 7,
      cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
      font: "helvetica",
      textColor: P.textDark,
      lineColor: P.border,
      lineWidth: 0.15,
      overflow: "ellipsize",
    },
    headStyles: {
      fillColor: P.blue,
      textColor: P.white,
      halign: "center",
      fontStyle: "bold",
      fontSize: 7,
      cellPadding: { top: 3, bottom: 3, left: 1.5, right: 1.5 },
    },
    alternateRowStyles: { fillColor: P.offWhite },
    bodyStyles: { fillColor: P.white },
    columnStyles: {
      0: { halign: "center", cellWidth: 7 },
      1: { cellWidth: 15 },
      2: { cellWidth: 39 },
      3: { cellWidth: 15 },
      4: { halign: "right", cellWidth: 9 },
      5: { cellWidth: 9 },
      6: { halign: "right", cellWidth: 16 },
      7: { halign: "right", cellWidth: 9 },
      8: { halign: "right", cellWidth: 19 },
      9: { halign: "right", cellWidth: 14 },
      10: { halign: "right", cellWidth: 15 },
      11: { halign: "right", cellWidth: 11 },
      12: { halign: "right", cellWidth: 13 },
      13: { halign: "right", cellWidth: 11 },
      14: { halign: "right", cellWidth: 11 },
      15: { halign: "right", cellWidth: 11 },
      16: { cellWidth: 13 },
      17: { cellWidth: 13 },
      18: { halign: "right", cellWidth: 19, fontStyle: "bold" },
    },
    pageBreak: "auto",
    margin: { left: M, right: M },
  });

  // ─── SUMMARY + WORDS SECTION ─────────────────────────────────────────────
  y = doc.lastAutoTable.finalY + 5;

  const SUM_W = 80;
  const SUM_X = PW - M - SUM_W;
  const WORD_W = SUM_X - M - 4;

  // Discount is deducted after the sales tax, which is charged on the
  // undiscounted value - so it never changes the "Sales Tax:" line above it.
  // The row is only printed when something was actually discounted, leaving
  // undiscounted invoices looking exactly as before.
  const totalDiscount = (data?.items || []).reduce(
    (sum, item) => sum + (Number(item?.discount) || 0),
    0,
  );

  const summaryRows = [
    { label: "Excl. Sales Tax Value", value: data?.subTotal || 0, grand: false },
    { label: "Sales Tax:", value: data?.taxTotal || 0, grand: false },
    ...(totalDiscount > 0
      ? [{ label: "Discount:", value: -totalDiscount, grand: false }]
      : []),
    { label: "Inclusive Sales Tax Value:", value: data?.grandTotal || 0, grand: true },
    // Section 236H comes after the invoice value and is never part of it; the
    // final amount is that value plus the 236H amount. Both are left out when
    // the invoice has no 236H rate.
    ...(hasSection236H(data?.GSection236H)
      ? [
          {
            label: `${section236HLabel(data?.GSection236H)}:`,
            value: section236HAmountFor(data?.grandTotal, data?.GSection236H),
            grand: false,
          },
          {
            label: `${SECTION_236H_FINAL_LABEL}:`,
            value: section236HFinalAmountFor(
              data?.grandTotal,
              data?.GSection236H,
            ),
            grand: true,
          },
        ]
      : []),
  ];

  const SUM_HDR_H = 8;
  const SUM_ROW_H = 9;
  const SUM_H = SUM_HDR_H + summaryRows.length * SUM_ROW_H + 1;

  // Sized off the card itself so an extra Discount row cannot spill off the
  // page (identical to the previous fixed 46 when no discount row is shown).
  needsNewPage(SUM_H + 10);

  // Summary card
  strokeRect(SUM_X, y, SUM_W, SUM_H, P.border, P.white);
  fillRect(SUM_X, y, SUM_W, SUM_HDR_H, P.navy);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...P.white);
  doc.text("INVOICE SUMMARY", SUM_X + SUM_W / 2, y + 5.5, { align: "center" });

  summaryRows.forEach((row, i) => {
    const ry = y + SUM_HDR_H + i * SUM_ROW_H;
    if (row.grand) {
      fillRect(SUM_X, ry, SUM_W, SUM_ROW_H, P.grandTotalBg);
      doc.setFont("Helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...P.white);
    } else {
      hLine(SUM_X + 2, SUM_X + SUM_W - 2, ry);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...P.textDark);
    }
    doc.text(row.label, SUM_X + 3, ry + 6);
    doc.text(
      String(displayTwoDecimalDigits(row.value)),
      SUM_X + SUM_W - 3,
      ry + 6,
      { align: "right" },
    );
  });

  // Amount in words card (aligned with summary)
  strokeRect(M, y, WORD_W, SUM_H, P.border, P.lightBlue);
  fillRect(M, y, WORD_W, SUM_HDR_H, P.navy);

  doc.setFont("Helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...P.white);
  doc.text("AMOUNT IN WORDS", M + 4, y + 5.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...P.textDark);

  let amountWords = "N/A";
  try {
    amountWords = `${toWords(Math.floor(data?.grandTotal || 0)).toUpperCase()} RUPEES ONLY`;
  } catch (_) {
    /* fallback already set */
  }

  const wordLines = doc.splitTextToSize(amountWords, WORD_W - 8);
  doc.text(wordLines, M + 4, y + SUM_HDR_H + 7);

  y += SUM_H + 6;
  needsNewPage(10);

  // ─── FOOTER NOTE ─────────────────────────────────────────────────────────
  hLine(M, PW - M, y, P.border);
  y += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...P.textMuted);
  doc.text(
    "This is a computer generated invoice. No signature is required.",
    PW / 2,
    y,
    { align: "center" },
  );

  // ─── PAGE FOOTER BAR (stamped on every page) ──────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    fillRect(0, PH - FOOTER_BAR_H, PW, FOOTER_BAR_H, P.navy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...P.blueFade);
    doc.text(`${companyName}  ·  FBR Ref: ${invoiceRef}`, M, PH - 3);
    doc.text(`Page ${pg} of ${totalPages}`, PW - M, PH - 3, { align: "right" });
  }

  // ─── OUTPUT ───────────────────────────────────────────────────────────────
  const pdfBlob = doc.output("blob");
  window.open(URL.createObjectURL(pdfBlob));
};
